// Cartão de um cliente na visão consolidada da agência.
import { Link } from 'react-router-dom'
import { Bot, Lightbulb, OctagonAlert, Plug, TriangleAlert } from 'lucide-react'
import { brl, num } from '@impulso/shared'
import type { AgencyClientRow } from '@impulso/shared/api/agency'

const MODE: Record<string, string> = { suggest: 'Só sugere', copilot: 'Copiloto', auto: 'Automático' }
const PACE: Record<string, { cls: string; text: string }> = {
  over: { cls: 'red', text: 'Acima do ritmo' },
  under: { cls: 'amber', text: 'Abaixo do ritmo' },
  on_track: { cls: 'green', text: 'No ritmo' },
  no_budget: { cls: '', text: 'Sem orçamento definido' },
}

export function ClientCard({ c, dayOfMonth, daysInMonth }: { c: AgencyClientRow; dayOfMonth: number; daysInMonth: number }) {
  const pace = PACE[c.pace] ?? PACE.no_budget!
  const pctSpent = c.monthlyBudget ? Math.min(100, (c.spendMonth / c.monthlyBudget) * 100) : 0
  const expectedPct = (dayOfMonth / daysInMonth) * 100
  const syncing = c.connections.active > 0 && !c.lastDataDate
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</h3>
          {c.segment && <div className="small muted">{c.segment}</div>}
        </div>
        {c.alerts.length === 0 ? <span className="badge green">Tudo certo</span> : <span className={`badge ${c.alerts.some((a) => a.severity === 'critical') ? 'red' : 'amber'}`}>Precisa de atenção</span>}
      </div>

      {c.alerts.length > 0 && (
        <ul className="stack" style={{ gap: 6, margin: 0, padding: 0, listStyle: 'none' }}>
          {c.alerts.map((a) => (
            <li key={a.kind} className="row small" style={{ gap: 8, alignItems: 'flex-start', color: a.severity === 'critical' ? 'var(--red)' : 'var(--amber)' }}>
              {a.severity === 'critical' ? <OctagonAlert size={15} style={{ flexShrink: 0, marginTop: 1 }} /> : <TriangleAlert size={15} style={{ flexShrink: 0, marginTop: 1 }} />}
              <span>{a.message}</span>
            </li>
          ))}
        </ul>
      )}

      <div>
        <div className="row between small">
          <span className="muted">Gasto no mês</span>
          <span><strong>{brl(c.spendMonth)}</strong>{c.monthlyBudget ? <span className="muted"> de {brl(c.monthlyBudget)}</span> : null}</span>
        </div>
        {c.monthlyBudget ? (
          <>
            <div className="progress mt-8" aria-label={`Gasto: ${Math.round(pctSpent)}% do orçamento`}>
              <span style={{ width: `${pctSpent}%`, background: c.pace === 'over' ? 'var(--red)' : undefined }} />
              <i className="marker" style={{ left: `calc(${expectedPct}% - 1px)` }} title="Onde deveria estar hoje" />
            </div>
            <div className="row between small mt-8">
              <span className={`badge ${pace.cls}`}>{pace.text}</span>
              <span className="muted">Previsão: {brl(c.projectedSpend)}</span>
            </div>
          </>
        ) : (
          <div className="small muted mt-8">{pace.text}. <Link to={`/app/${c.companyId}/config/negocio`} style={{ color: 'var(--primary)' }}>Definir</Link></div>
        )}
      </div>

      <div className="grid g-2" style={{ gap: 10 }}>
        <div><div className="small muted">Resultados no mês</div><div className="strong">{syncing ? 'Sincronizando…' : num(c.results)}</div></div>
        <div><div className="small muted">Custo por resultado</div><div className="strong">{c.costPerResult != null ? brl(c.costPerResult, 2) : '—'}</div></div>
      </div>

      <div className="row wrap small" style={{ gap: 8 }}>
        <span className="chip" style={{ padding: '4px 10px' }}><Lightbulb size={14} /> {c.suggestions.open} sugest{c.suggestions.open === 1 ? 'ão' : 'ões'}{c.suggestions.high > 0 ? ` · ${c.suggestions.high} importante${c.suggestions.high === 1 ? '' : 's'}` : ''}</span>
        <span className="chip" style={{ padding: '4px 10px' }}><Bot size={14} /> {c.autopilot.paused ? 'Piloto pausado' : MODE[c.autopilot.mode] ?? c.autopilot.mode}</span>
        <span className="chip" style={{ padding: '4px 10px' }}><Plug size={14} /> {c.connections.active} conectada{c.connections.active === 1 ? '' : 's'}{c.connections.needsReconnect > 0 ? ` · ${c.connections.needsReconnect} com problema` : ''}</span>
      </div>

      <div className="row wrap" style={{ gap: 8, marginTop: 'auto' }}>
        <Link className="btn primary sm" to={`/app/${c.companyId}/inicio`}>Abrir</Link>
        <Link className="btn sm" to={`/app/${c.companyId}/contas`}>Contas</Link>
        <Link className="btn sm" to={`/app/${c.companyId}/sugestoes`}>Sugestões</Link>
      </div>
    </div>
  )
}
